import React, { useCallback } from 'react'
import { Dropdown, Button, Popover, Menu, Spin, Icon, Tooltip } from 'antd'
import ReactPlayer from 'react-player'
import CommentInput from '../../../CommentInput'
import TransitionSelector from '../Transition/TransitionSelector'
import TransitionDisplay from '../Transition/TransitionDisplay'
import {
  DownOutlined,
  ReloadOutlined,
  SettingOutlined,
  UploadOutlined
} from '@ant-design/icons'
import _ from 'lodash'

// Shot component abstracts a single video shot within a scene
const Shot = ({
  shot,
  sceneId,
  index,
  isLastShot,
  formData,
  transitions,
  handleRegenerateVideo,
  setScenes,
  handleUploadVideo,
  handleEditSettings,
  handleTransitionSelect,
  isGeneratingAll = false,
  videoGenerating = []
}) => {
  const isVideoGenerating = videoGenerating.some(
    status => status.sceneId === sceneId && status.shotId === shot.id
  )

  const debouncedUpdateShotData = useCallback(
    _.debounce((key, value) => {
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id === sceneId) {
            return {
              ...scene,
              shots: scene.shots.map(s => {
                if (s.id === shot.id) {
                  return {
                    ...s,
                    [key]: value
                  }
                }
                return s
              })
            }
          }
          return scene
        })
      )
    }, 300),
    [sceneId, shot.id]
  )

  const isError = shot && shot.errorGeneratingVideo

  return (
    <React.Fragment>
      <div className={`shot-card ${isError ? 'error-generating-video' : ''}`}>
        <h3 className="shot-title">Shot {index + 1}
          {isError &&
            <Tooltip title="Video generation error maybe cause by bad prompt or a network issue. You can regenerate the video from Actions menu.">
              <span style={{ color: 'red', border: '1px solid red', borderRadius: '5px', padding: '5px', marginLeft: '5px' }}> <Icon type="info-circle" /> Error! </span>
            </Tooltip>}
        </h3>
        <div className="video-wrapper">
          {shot.vidSrc ? (
            <ReactPlayer
              url={shot.vidSrc}
              width="100%"
              height="250px"
              controls={true}
              config={{
                file: {
                  attributes: {
                    controlsList: 'nodownload' // Disable download option
                  }
                }
              }}
            />
          ) : (
            <img
              src={shot.imageSrc}
              width="100%"
              height="250px"
              style={{ objectFit: 'contain' }}
            />
          )}
        </div>
        <CommentInput
          characters={formData.characterList}
          text={shot.shot_direction}
          placeholder="Write your video prompt..."
          disabled={isVideoGenerating || isGeneratingAll}
          onChange={value => debouncedUpdateShotData('shot_direction', value)}
        />
        <div className="card-actions">
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu className="video-generation-menu">
                <Menu.Item
                  onClick={() => handleRegenerateVideo(sceneId, shot.id)}
                >
                  <ReloadOutlined /> Regenerate Video
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    document
                      .getElementById(`upload-input-${sceneId}-${shot.id}`)
                      .click()
                  }
                >
                  <UploadOutlined /> Upload Your Own Video
                  <input
                    id={`upload-input-${sceneId}-${shot.id}`}
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={e => {
                      const file = e.target.files[0]
                      if (file) handleUploadVideo(sceneId, shot.id, file)
                      e.target.value = null // Reset input to allow same file selection
                    }}
                  />
                </Menu.Item>
              </Menu>
            }
            disabled={isVideoGenerating || isGeneratingAll}
          >
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>

          {/* <Button
            disabled={isVideoGenerating || isGeneratingAll}
            onClick={() => handleEditSettings(sceneId, shot.id)}
          >
            <SettingOutlined /> Settings
          </Button> */}
        </div>

        {isVideoGenerating && (
          <div className="video-generating-overlay">
            <Spin size="large" tip="Generating..." />
          </div>
        )}
      </div>

      {/* Only show between-shots transition if it's not the last shot */}
      {!isLastShot && (
        <div className="transition-wrapper">
          <Popover
            content={
              <TransitionSelector
                onSelect={type =>
                  handleTransitionSelect(sceneId, shot.id, type)
                }
                currentTransition={transitions[`${sceneId}-${shot.id}`]}
              />
            }
            trigger="hover"
            placement="right"
          >
            <div className="transition-trigger">
              <TransitionDisplay
                type={transitions[`${sceneId}-${shot.id}`]}
                onRemove={() => handleTransitionSelect(sceneId, shot.id, null)}
              />
            </div>
          </Popover>
        </div>
      )}
    </React.Fragment>
  )
}

export default Shot
